import { FormControlLabel, Radio, RadioGroup, TextField } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { esES, zhCN } from "@mui/x-date-pickers/locales";
import { ReactComponent as CalendarIcon } from "../../../Assets/Calendar.svg";
import classNames from "classnames";
import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { stateConfig } from "../../../configs/state";
import { UserData } from "../../../types/user-data";
import useCheckScreenSize from "../../../utils/checkScreenSize";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import "dayjs/locale/zh-tw";

import "../../styles.css";
import { InfoBanner } from "../../../utils/InfoBanner";
import moment from "moment";
import { useTranslation } from "react-i18next";

interface Props extends UserData {
  updateFields: (fields: Partial<UserData>) => void;
  data: Partial<UserData>;
}

export const IncidentInfo: React.FC<Props> = ({ updateFields, data }) => {
  const { t, i18n } = useTranslation();
  const openTimePicker = () => {
    const input = document.getElementById("estimatedTimeofIncident") as HTMLInputElement | null;
    if (!input) return;
    if (typeof input.showPicker === "function") { input.showPicker(); } else { input.focus(); }
  };
  const {
    register,
    resetField,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    shouldFocusError: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const isMobile = useCheckScreenSize({ breakpoint: 850 });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dateValue, setDateValue] = useState<Dayjs | null>(null);
  const resolvedLanguage = (i18n.resolvedLanguage || "en").toLowerCase();
  const isChinese = resolvedLanguage.startsWith("zh");
  const isSpanish = resolvedLanguage.startsWith("es");
  const datePickerLocale = isChinese ? "zh-tw": isSpanish? "es": "en";
  const datePickerLocaleText = isChinese ? zhCN.components.MuiLocalizationProvider.defaultProps.localeText: isSpanish
        ? esES.components.MuiLocalizationProvider.defaultProps.localeText
        : undefined;

  const handleNext = () => {
    setSubmitted(true);
    if (
      data.incident_date &&
      !isInTheFuture(new Date(data?.incident_date!)) &&
      !isInThePast(new Date(data?.incident_date!)) &&
      dateValue !== null &&
      /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(data.incident_date as string)
    ) {
      navigate("../ClaimDetails");
    }
  };

  const handleError  = () => {
    if((!data.incident_date && submitted) ||
    (dateValue === null && submitted) ||
    (data.incident_date &&
      !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(
        data.incident_date as string
      )) ||
    isInTheFuture(new Date(data?.incident_date!))||
    isInThePast(new Date(data?.incident_date!))){
      document.getElementById('estimatedDateofIncident')?.focus();
    }
    else if(errors.incident_time){
      document.getElementById('estimatedTimeofIncident')?.focus();
    }
    else if(errors.same_as_mailing_address){
      document.getElementById('sameasMailingAddress_error')?.focus();
    }
    else if(errors.incident_location){
      document.getElementById('location_error')?.focus();
    }
    else if(errors.incident_address_line1){
      document.getElementById('incidentAddress')?.focus();
    }
    else if(errors.address_line2){
      document.getElementById('apt')?.focus();
    }
    else if(errors.incident_city){
      document.getElementById('city')?.focus();
    }
    else if(errors.incident_zip_code){
      document.getElementById('zipCode')?.focus();
    }
    else if(errors.incident_description){
      document.getElementById('description')?.focus();
    }
    else if(errors.incident_city1){
      document.getElementById('city1')?.focus();
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  function DateIcon(props: any) {
    return (
      <span style={{ marginRight: "5px", marginLeft: "5px" }}
        title={String(t('Calendar'))}
      >
        <CalendarIcon {...props} />
      </span>
    );
  }
  const submit = () => {
    setSubmitted(true);
    if((!data.incident_date && submitted) ||
    (dateValue === null && submitted) ||
    (data.incident_date &&
      !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(
        data.incident_date as string
      )) ||
    isInTheFuture(new Date(data?.incident_date!))||
    isInThePast(new Date(data?.incident_date!))){
      document.getElementById('estimatedDateofIncident')?.focus();
    }   
  };
  const handleBack = () => {
    navigate("../ClaimantInfo");
  };
  function isInTheFuture(date: string | Date) {
    const today = new Date();

    return date > today;
  }
 function isInThePast(date: string | Date) {
    const pastDate = new Date("2000-01-01");

    return date < pastDate;
  }

  useEffect(() => {
    if (dateValue === null && data.incident_date) {
      setDateValue(dayjs(data.incident_date));
    }
    if (isInTheFuture(new Date(data?.incident_date!))) {
      setDateValue(null);
    }
    if (isInThePast(new Date(data?.incident_date!))) {
      setDateValue(null);
    }
  }, []);

  function resetFields() {
    resetField("incident_address_line1");
    data.incident_address_line1 = "";
    resetField("incident_address_line2");
    data.incident_address_line2 = "";
    resetField("incident_city");
    data.incident_city = "";
    resetField("incident_zip_code");
    data.incident_zip_code = "";
    resetField("incident_description");
    data.incident_description = "";
  }

  return (
    <>
      <form onSubmit={handleSubmit(handleNext,handleError)}>
        <div>
          <h2 className="subheader">{t('IncidentInformationHeading')}</h2>
          <div style={{ marginBottom: "20px" }}>{t('RequiredField')} </div>
          <div className="formLabel">
            <label htmlFor="estimatedDateofIncident">
              *{t('EstimatedDateOfIncident')}
            </label>
          </div>
          <div>
            <LocalizationProvider
              key={datePickerLocale}
              dateAdapter={AdapterDayjs}
              adapterLocale={datePickerLocale}
              localeText={datePickerLocaleText}
            >
              <Controller
                name="date"
                control={control}
                render={({ field, ...props }) => {
                  return (
                    <DesktopDatePicker
                      open={calendarOpen}
                      onOpen={() => setCalendarOpen(true)}
                      onClose={() => setCalendarOpen(false)}
                      inputFormat="MM/DD/YYYY"
                      value={dateValue}
                      onChange={(value) => {
                        setDateValue(value);
                        updateFields({
                          incident_date: moment(value?.format())
                            .utc()
                            .format("MM/DD/YYYY"),
                        });
                      }}
                      components={{
                        OpenPickerIcon: DateIcon,
                      }}
                      componentsProps={{
                        actionBar: {
                          actions: ["today"],
                        },
                      }}
                      disableFuture
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          id="estimatedDateofIncident"
                          aria-labelledby="estimatedDateofIncident estimatedDateofIncident_error"
                          inputProps={{
                            ...params.inputProps,
                            placeholder: String(t('IncDate')),
                          }}
                          sx={
                            (!data.incident_date && submitted) ||
                            (dateValue === null && submitted) ||
                            (data.incident_date &&
                              !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(
                                data.incident_date as string
                              )) ||
                            isInTheFuture(new Date(data?.incident_date!))||
                            isInThePast(new Date(data?.incident_date!))
                              ? {
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "#e2231a",
                                      border: "2.5px solid #e2231a",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#e2231a",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#e2231a",
                                    },
                                    width: "344px",
                                    height: "2.7em",
                                  },
                                }
                              : {
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "#4f4f4f",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#0c7dbb",
                                    },
                                    width: "344px",
                                    height: "2.7em",
                                  },
                                }
                          }
                        />
                      )}
                    />
                  );
                }}
              />
            </LocalizationProvider>
          </div>
          {(!data.incident_date && submitted) ||
          (dateValue === null && submitted) ? (
            <div className="errorText" id="estimatedDateofIncident_error">
              {t('ErrorPleaseEnterValidDate')}
            </div>
          ) : null}

          {data.incident_date &&
          dateValue !== null &&
          !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(data.incident_date as string) ? (
            <div className="errorText" id="estimatedDateofIncident_error">
              {t('ErrorValidDateFormat')}
            </div>
          ) : null}
          {data.incident_date &&
          dateValue !== null &&
          isInTheFuture(new Date(data?.incident_date!)) ? (
            <div className="errorText" id="estimatedDateofIncident_error">
              {t('ErrorFutureDateNotAllowed')}
            </div>
          ) : null}
            {data.incident_date &&
          dateValue !== null &&
          isInThePast(new Date(data?.incident_date!)) ? (
            <div className="errorText" id="estimatedDateofIncident_error">
              {t('ErrorYearCurrentCentury')}
            </div>
          ) : null}
          <div className="formLabel">
            <label htmlFor="estimatedTimeofIncident">
              {t('EstimatedTimeOfIncident')}
            </label>
          </div>

          <div style={{ position: "relative", display: "inline-block" }}>
          <input
            id="estimatedTimeofIncident"
            className={classNames({
              errorInput: errors.incident_time,
              input: !errors.incident_time,
            })}
            {...register("incident_time", {
              required: false,
              validate: {
                validTime: (value) =>
                  !value || /^([01]\d|2[0-3]):([0-5]\d)$/.test(value),
              }, 
              onChange: (e) => {
                updateFields({ incident_time: e.target.value });
              },
            })}
            type="time"
              aria-label={String(t('ShowTimePicker'))}
              title={String(t('ShowTimePicker'))}
            onInvalid={(event) => {
              (event.target as HTMLInputElement).setCustomValidity(
                String(t("ErrorValidTime12Hour"))
              );
            }}
            onInput={(event) => {
              (event.target as HTMLInputElement).setCustomValidity("");
            }}
            // onFocus={(event) => {
            //   (event.target as HTMLInputElement).setCustomValidity("");
            // }}
            // onMouseEnter={(event) => {
            //   (event.target as HTMLInputElement).setCustomValidity("");
            // }}
            value={data.incident_time}
            aria-describedby="estimatedTimeofIncident_error"
          />
            <span
              title={String(t('ShowTimePicker'))}
              onClick={openTimePicker}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openTimePicker(); } }}
              role="button"
              tabIndex={0}
              aria-label={String(t('ShowTimePicker'))}
              style={{ position: "absolute", top: 0, right: 0, width: "40px", height: "100%", cursor: "pointer" }}
            />
          </div>
          {errors.incident_time ? (
            <div className="errorText" id="estimatedTimeofIncident_error">
              {t('ErrorValidTime12Hour')}
            </div>
          ) : null}

          <div className="formLabel">
            <label id="sameasMailingAddress" htmlFor="mailAdrLabel">*{t('SameAsMailingAddress')}</label>
          </div>
          <RadioGroup
            style={{ marginTop: "5px", marginBottom: "10px" }}
            id="mailAdrLabel"
            role="radiogroup"
            aria-labelledby="sameasMailingAddress"
            aria-describedby="sameasMailingAddress_error"
          >
            <FormControlLabel
              sx={{ height: "28px" }}
              {...register("same_as_mailing_address", {
                required: true,
                onChange: (e) => {
                  resetFields();
                  updateFields({ same_as_mailing_address: e.target.value });
                },
                validate: (value) => value === "No" || value === "Yes",
              })}
              value="Yes"
              checked={data.same_as_mailing_address === "Yes"}
              control={
                <Radio
                  sx={errors.same_as_mailing_address && { color: "#e2231a" }}
                />
              }
              label={t('Yes')}
              id="sameasMailingAddress_error"
            />
            <FormControlLabel
              {...register("same_as_mailing_address", {
                required: true,
                onChange: (e) => {
                  resetFields();
                  updateFields({ same_as_mailing_address: e.target.value });
                },
                validate: (value) => value === "No" || value === "Yes",
              })}
              value="No"
              checked={data.same_as_mailing_address === "No"}
              control={
                <Radio
                  sx={errors.same_as_mailing_address && { color: "#e2231a" }}
                />
              }
              label={t('No')}
              id="sameasMailingAddress_error"
            />
          </RadioGroup>
          {errors.same_as_mailing_address ? (
            <div className="errorText" id="sameasMailingAddress_error">
              {t('ErrorSameAsMailingRequired')}
            </div>
          ) : null}

          {data.same_as_mailing_address === "No" ? (
            <div>
              <div className="formLabel">
                <label id="location" htmlFor="locationLabel">{t('Location')}</label>
              </div>
              <RadioGroup
                style={{ marginTop: "5px", marginBottom: "10px" }}
                id="locationLabel"
                role="radiogroup"
                aria-labelledby="location"
                aria-describedby="location_error"
              >
                <FormControlLabel
                  sx={{ height: "28px" }}
                  {...register("incident_location", {
                    required: true,
                    onChange: (e) => {
                      resetFields();
                      updateFields({ incident_location: e.target.value });
                    },
                    validate: (value) =>
                      value === "StreetAddress" ||
                      value === "OtherProvideLocationDescription",
                  })}
                  checked={data.incident_location === "StreetAddress"}
                  value="StreetAddress"
                  control={
                    <Radio
                      sx={errors.incident_location && { color: "#e2231a" }}
                    />
                  }
                  label={t('StreetAddress')}
                  id="location_error"
                />
                <FormControlLabel
                  {...register("incident_location", {
                    required: true,
                    onChange: (e) => {
                      resetFields();
                      updateFields({ incident_location: e.target.value });
                    },
                    validate: (value) =>
                      value === "StreetAddress" ||
                      value === "OtherProvideLocationDescription",
                  })}
                  checked={
                    data.incident_location === "OtherProvideLocationDescription"
                  }
                  value="OtherProvideLocationDescription"
                  control={
                    <Radio
                      sx={errors.incident_location && { color: "#e2231a" }}
                    />
                  }
                  label={t('OtherDescribe')}
                  id="location_error"
                />
              </RadioGroup>
              {errors.incident_location ? (
                <div className="errorText" id="location_error">
                  {t('ErrorOneSelectionRequired')}
                </div>
              ) : null}
            </div>
          ) : null}

          {data.incident_location === "StreetAddress" &&
          data.same_as_mailing_address === "No" ? (
            <>
              <div className={classNames({ row: !isMobile })}>
                <div className="column" style={{ marginRight: "2%" }}>
                  <div className="formLabel">
                    <label htmlFor="incidentAddress">*{t('IncidentAddress')}</label>
                  </div>
                  <input
                    id="incidentAddress"
                    className={classNames(
                      errors.incident_address_line1 && !isMobile
                        ? "errorInputMail"
                        : "inputMail",
                      errors.incident_address_line1 && isMobile
                        ? "errorInputMailNoWidth"
                        : "inputMail",
                      isMobile ? "inputMailMobile" : "input"
                    )}
                    {...register("incident_address_line1", {
                      required: true,
                      onChange: (e) =>
                        updateFields({
                          incident_address_line1: e.target.value
                            .trimStart()
                            .replaceAll(/[-~`!^;|\\":<>?()@$*{}+=]/gi, ""),
                        }),
                    })}
                    value={data.incident_address_line1}
                    maxLength={100}
                    aria-describedby="incidentAddress_error"
                  />
                  {errors.incident_address_line1 ? (
                    <div className="errorText" id="incidentAddress_error">
                      {t('ErrorIncidentAddressRequired')}
                    </div>
                  ) : null}
                </div>
                <div className="column" style={{ marginRight: "2%" }}>
                  <div className="formLabel">
                    <label htmlFor="apt">{t('AptSuite')}</label>
                  </div>
                  <input
                    id="apt"
                    className={classNames(
                      errors.address_line2 ? "errorInputSmall" : "inputSmall",
                      isMobile ? "input" : "inputSmall"
                    )}
                    onChange={(e) =>
                      updateFields({
                        incident_address_line2: e.target.value
                          .trimStart()
                          .replaceAll(/[-~`!^;|\\":<>?()@$*{}+=]/gi, ""),
                      })
                    }
                    value={data.incident_address_line2}
                  />
                </div>
              </div>
              <div className={classNames({ row: !isMobile })}>
                <div className="column" style={{ marginRight: "2%" }}>
                  <div className="formLabel">
                    <label htmlFor="city">*{t('City')} </label>
                  </div>
                  <input
                    id="city"
                    className={classNames({
                      errorInputCity: errors.incident_city,
                      inputCity: !errors.incident_city,
                    })}
                    {...register("incident_city", {
                      required: true,
                      onChange: (e) =>
                        updateFields({
                          incident_city: e.target.value
                            .trimStart()
                            .replaceAll(/[-~`!^;|\\":<>?()@$*{}+=]/gi, ""),
                        }),
                    })}
                    value={data.incident_city}
                    maxLength={50}
                    aria-describedby="city_error"
                  />
                  {errors.incident_city ? (
                    <div className="errorText" id="city_error">
                      {t('ErrorCityRequired')}
                    </div>
                  ) : null}
                </div>
                <div className="column" style={{ marginRight: "2%" }}>
                  <div className="formLabel">
                    <label htmlFor="zipCode">*{t('ZIPCode')}</label>
                  </div>
                  <input
                    id="zipCode"
                    maxLength={5}
                    className={classNames({
                      errorInputSmall: errors.incident_zip_code,
                      inputSmall: !errors.incident_zip_code,
                    })}
                    {...register("incident_zip_code", {
                      required: true,
                      validate: { validateNumber: (v) => v.length === 5 },
                      onChange: (e) =>
                        updateFields({
                          incident_zip_code: e.target.value
                            .trim()
                            .replaceAll(/[^0-9 ]/gi, ""),
                        }),
                    })}
                    value={data.incident_zip_code}
                    aria-describedby="zip_code_error"
                  />
                  {errors.incident_zip_code ? (
                    <div
                      className={classNames("errorText", {
                        absolute: !isMobile,
                      })}
                      id="zip_code_error"
                    >
                      {t('ErrorZipCodeRequired')}
                    </div>
                  ) : null}
                </div>
                <div className="column" style={{ marginRight: "3%" }}>
                  <div className="formLabel">
                    <label htmlFor="stateView">*{t('State')}</label>
                  </div>
                  <select
                    className="inputSmallState"
                    id="state"
                    defaultValue={data.incident_state}
                    value={data.incident_state}
                    onChange={(e) =>
                      updateFields({ incident_state: e.target.value })
                    }
                  >
                    {stateConfig.map((state, index) => (
                      <option key={index} value={state.key}>
                        {" "}
                        {state.key}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          ) : null}

          {data.incident_location === "OtherProvideLocationDescription" &&
          data.same_as_mailing_address === "No" ? (
            <div>
              <div className="formLabel">
                <label htmlFor="description">*{t('DescriptionOfLocation')}</label>
              </div>
              <textarea
                id="description"
                className={classNames({
                  errorTextarea: errors.incident_description,
                  textarea: !errors.incident_description,
                })}
                {...register("incident_description", {
                  required: true,
                  onChange: (e) =>
                    updateFields({
                      incident_description: e.target.value
                        .trimStart()
                        .replaceAll(/[-~`!^;|\\":<>?()@$*{}+=]/gi, ""),
                    }),
                })}
                maxLength={300}
                rows={6}
                cols={42}
                value={data.incident_description}
                aria-describedby="descriptionlocation_error"
              />
              <br />

              <div aria-live="polite" role="alert">
                {t('YouHave')} <strong>{300 - data.incident_description!.length}</strong>{" "}
                {t('CharactersRemaining')}
              </div>

              {errors.incident_description ? (
                <div className="errorText" id="descriptionlocation_error">
                  {t('ErrorLocationDescriptionRequired')}
                </div>
              ) : null}
              <div className={classNames({ row: !isMobile })}>
                <div className="column" style={{ marginRight: "2%" }}>
                  <div className="formLabel">
                    <label htmlFor="city1">*{t('City')}</label>
                  </div>

                  <input
                    id="city1"
                    className={classNames(
                      errors.incident_city1 ? "errorInputCity" : "inputCity",
                      isMobile ? "input" : "inputCity"
                    )}
                    {...register("incident_city1", {
                      required: true,
                      onChange: (e) =>
                        updateFields({
                          incident_city: e.target.value
                            .trimStart()
                            .replaceAll(/[-~`!^;|\\":<>?()@$*{}+=]/gi, ""),
                        }),
                    })}
                    value={data.incident_city}
                    aria-describedby="city1_error"
                  />
                  {errors.incident_city1 ? (
                    <div className="errorText" id="city1_error">
                      {t('ErrorCityRequired')}
                    </div>
                  ) : null}
                </div>
                <div className="column" style={{ marginRight: "3%" }}>
                  <div className="formLabel">
                    <label htmlFor="state">*{t('State')}</label>
                  </div>
                  <select
                    className="inputSmallState"
                    id="state"
                    defaultValue={data.incident_state}
                    value={data.incident_state}
                    onChange={(e) =>
                      updateFields({ incident_state: e.target.value })
                    }
                  >
                    {stateConfig.map((state, index) => (
                      <option key={index} value={state.key}>
                        {" "}
                        {state.key}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <br />
        <br />

        <button className="yellowButton" type="submit" onClick={submit}>
          {t('Next')}
        </button>
        <div className="secondButtonDiv">
          <button onClick={handleBack} className="secondButton">
            {t('Back')}
          </button>
        </div>
      </form>
      <InfoBanner />
    </>
  );
};
